import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import replace from 'lodash/replace';
import startsWith from 'lodash/startsWith';
import { useEffect, useState } from 'react';

/**
 * Fetches JSON data from a URL (typically a presigned S3 URL).
 *
 * Used by chart/table components that receive a `dataUrl` prop pointing to
 * a data file uploaded via the chatbot-data:// placeholder pattern.
 * Returns the parsed JSON array, or the inline `fallback` data while loading
 * or if the URL is an unresolved placeholder.
 */
export function useDataUrl (
  dataUrl: string | undefined,
  fallback: unknown[] | undefined,
): unknown[] {
  const [fetched, setFetched] = useState<unknown[] | null>(null);

  useEffect(() => {
    // Skip unresolved chatbot-data:// placeholders during streaming
    if (!dataUrl || startsWith(dataUrl, 'chatbot-data://')) {
      setFetched(null);
      return;
    }

    let cancelled = false;

    fetch(dataUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        // Use text() instead of json() so we can sanitize NaN/Infinity
        // values that pandas may emit — these are invalid JSON and would
        // cause JSON.parse() to throw.
        return res.text();
      })
      .then((text) => {
        const sanitized = replace(
          replace(replace(text, /\bNaN\b/g, 'null'), /-Infinity\b/g, 'null'),
          /\bInfinity\b/g, 'null',
        );
        const data = JSON.parse(sanitized);
        // Server may return a non-array (string, number, object literal) if
        // the URL is malformed or pointed at a wrong endpoint. Chart/table
        // components downstream call `.map()` on the value and crash with
        // "displayedData.map is not a function". Coerce to null so the
        // `?? fallback ?? []` path triggers a clean empty render instead.
        // Regression: scripts/fuzz_genui/results.tsv row 060.
        if (!cancelled) setFetched(isArray(data) ? data : null);
      })
      .catch(() => {
        // Fall back to inline data on fetch error
        if (!cancelled) setFetched(null);
      });

    return () => {
      cancelled = true;
    };
  }, [dataUrl]);

  // Inline `fallback` (i.e. props.data) can also be a non-array (object,
  // string, number) when the LLM emits a malformed payload. Downstream
  // chart/table components hit "displayedData.map is not a function". Coerce
  // here so every consumer of useDataUrl always sees an array.
  // Regression: scripts/fuzz_genui/results.tsv row 099.
  if (!isNil(fetched)) return fetched;
  return isArray(fallback) ? fallback : [];
}
